import { api, type HistoryItem, type UpdateItem } from '../api';
import { requireAuthOrRedirect } from '../auth';
import { renderNav } from '../nav';
import { coverEl, fmtDateGroup, fmtRelative, toast, pluginContentTypes, workHref } from '../utils';
import { getCachedSlug, warmSlugCache } from '../pretty-cache';
import { chapterUrlSuffix } from '../chapter-volume';
import { prettyWorkPath } from '../pretty-path';

const HIDE_READ_KEY = 'updates-hide-read';

/**
 * Посилання на розділ ІЗ ЗБЕРЕЖЕНОЮ ПОЗИЦІЄЮ ЧИТАННЯ, якщо вона є в історії
 * (наприклад, читач уже почав цей "новий" розділ, але не дочитав). Без
 * progress/page reader завжди відкриває розділ "з нуля".
 *
 * Для anime (як і в history.ts) окремої читалки не використовується — плеєр
 * вбудований прямо в сторінку твору, тож ведемо на неї.
 */
export function readerUrl(u: UpdateItem, contentTypes: Record<string, 'novel' | 'manga' | 'anime'>, historyEntry?: HistoryItem): string {
  if (contentTypes[u.pluginId] === 'anime') {
    return workHref(u.pluginId, u.novelPath, 'anime', '&from=updates');
  }
  const extra = new URLSearchParams({ from: 'updates' });
  if (historyEntry && historyEntry.chapterPath === u.chapterPath) {
    if (historyEntry.page) {
      extra.set('page', String(historyEntry.page));
    } else if (historyEntry.progress > 0 && historyEntry.progress < 100) {
      extra.set('progress', String(historyEntry.progress));
    }
  }

  const cachedSlug = getCachedSlug(u.pluginId, u.novelPath);
  if (cachedSlug && u.chapterNumber !== undefined) {
    return `${prettyWorkPath(u.pluginId, cachedSlug, chapterUrlSuffix(u.chapterNumber))}?${extra.toString()}`;
  }

  const params = new URLSearchParams({
    plugin: u.pluginId,
    novelPath: u.novelPath,
    novelName: u.novelName,
    path: u.chapterPath,
    name: u.chapterName,
  });
  if (u.chapterNumber !== undefined) params.set('num', String(u.chapterNumber));
  if (u.cover) params.set('cover', u.cover);
  for (const [k, v] of extra) params.set(k, v);
  return `/reader?${params.toString()}`;
}

/**
 * ЗБЕРЕЖЕНА СТАРА ВЕРСІЯ сторінки «Нове» (до редизайну v2). Не змінювати — це точка
 * відкату: див. updates.ts (перемикач UPDATES_V2_ENABLED) і docs/updates-v2.md.
 */
export async function initUpdatesLegacy() {
  if (!(await requireAuthOrRedirect())) return;
  renderNav('updates');
  const contentTypes = await pluginContentTypes();

  const list = document.getElementById('list') as HTMLElement;
  const empty = document.getElementById('empty') as HTMLElement;
  const lastCheckEl = document.getElementById('last-check') as HTMLElement;
  const filterToggle = document.getElementById('filter-toggle') as HTMLButtonElement;
  const checkBtn = document.getElementById('check-btn') as HTMLButtonElement;

  let items: UpdateItem[] = [];
  let lastUpdateCheck: string | null = null;
  let historyByNovel = new Map<string, HistoryItem>();
  let hideRead = localStorage.getItem(HIDE_READ_KEY) === '1';
  filterToggle.classList.toggle('active', hideRead);

  function paintLastCheck() {
    lastCheckEl.textContent = lastUpdateCheck
      ? `Останнє оновлення бібліотеки: ${fmtRelative(lastUpdateCheck)}`
      : 'Бібліотеку ще не перевіряли на оновлення.';
  }

  function render() {
    const entries = hideRead ? items.filter((u) => !u.read) : items;

    list.innerHTML = '';
    if (!entries.length) {
      empty.style.display = 'flex';
      list.style.display = 'none';
      return;
    }
    empty.style.display = 'none';
    list.style.display = 'block';

    let currentGroup = '';
    for (const u of entries) {
      const group = fmtDateGroup(u.checkedAt);
      if (group !== currentGroup) {
        currentGroup = group;
        const label = document.createElement('div');
        label.className = 'section-label';
        label.textContent = group;
        list.appendChild(label);
      }

      const a = document.createElement('a');
      a.className = `row update-row${u.read ? ' update-read' : ''}`;
      a.href = readerUrl(u, contentTypes, historyByNovel.get(u.novelId));

      const cover = coverEl(u.novelName, u.cover, u.novelId);
      cover.classList.add('cover');

      const meta = document.createElement('div');
      meta.className = 'meta';
      const title = document.createElement('p');
      title.className = 'title';
      title.textContent = u.novelName;
      const sub = document.createElement('p');
      sub.className = 'sub';
      if (!u.read) {
        const dot = document.createElement('span');
        dot.className = 'update-dot';
        sub.appendChild(dot);
      }
      sub.appendChild(document.createTextNode(u.chapterName));
      meta.appendChild(title);
      meta.appendChild(sub);

      a.appendChild(cover);
      a.appendChild(meta);
      list.appendChild(a);
    }
  }

  async function load() {
    try {
      const [res, history] = await Promise.all([api.updates(), api.history().catch(() => [])]);
      items = res.items;
      lastUpdateCheck = res.lastUpdateCheck;
      historyByNovel = new Map(history.map((h) => [h.novelId, h]));
    } catch (err) {
      toast((err as Error).message);
    }
    paintLastCheck();
    render();
    warmSlugCache(
      items.map((u) => ({ pluginId: u.pluginId, path: u.novelPath, title: u.novelName })),
      render,
    );
  }

  filterToggle.addEventListener('click', () => {
    hideRead = !hideRead;
    localStorage.setItem(HIDE_READ_KEY, hideRead ? '1' : '0');
    filterToggle.classList.toggle('active', hideRead);
    render();
  });

  checkBtn.addEventListener('click', async () => {
    checkBtn.disabled = true;
    checkBtn.classList.add('spinning');
    try {
      const res = await api.checkUpdates();
      items = res.items;
      lastUpdateCheck = res.lastUpdateCheck;
      toast(res.newUpdates ? `Знайдено нових розділів: ${res.newUpdates}` : 'Нових розділів не знайдено');
      paintLastCheck();
      render();
      warmSlugCache(
        items.map((u) => ({ pluginId: u.pluginId, path: u.novelPath, title: u.novelName })),
        render,
      );
    } catch (err) {
      toast((err as Error).message);
    } finally {
      checkBtn.disabled = false;
      checkBtn.classList.remove('spinning');
    }
  });

  await load();
}
